import { timeParse } from "d3";
import { apiService } from "@/_services";
import {
  additionalYSpace,
  formatDate_DDMMMYHMS,
  formatDate_YMD,
  longShortTransform,
  timeParseNull2,
} from "@/_core/_helpers/_helpers";
import {
  buildPortfolioSimulations,
  createExcelForStrategy,
  highlightOnOff,
  readyToSimulate,
  writeStrategySummary,
} from "@/_core/data/Optimizer";
import moment from "moment";

// === Volatility Calculation === //
export function getVolatilityIrregular(
  _this,
  data,
  fieldName,
  fieldShowLoadingName,
) {
  _this[fieldShowLoadingName] = true;
  _this[fieldName] = 0;

  let params = {
    method: "POST",
    endpoint: "volatility_calculation",
    content_type: "application/json",
    query_params: {
      format: "json",
      rates: JSON.stringify(data),
    },
  };
  return apiService
    .getAll(params)
    .then((response) => {
      _this[fieldName] = response;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === CURRENCY: Simulate === //
export function simulatePortfolio(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "POST",
    endpoint: "currency_simulate_portfolio",
    content_type: "application/json",
    query_params: {
      format: "json",
      spot: _this.simRate,
      currency: _this.currencyNumber,
      confidence: _this.confidence / 100,
      expo: JSON.stringify(_this.expoDataRaw),
      hedge: JSON.stringify(_this.hedgeDataRaw),
    },
  };
  return apiService
    .getAll(params)
    .then((response) => {
      response = JSON.parse(JSON.stringify(response));
      response.forEach(function(d) {
        d.date = timeParseNull2(d.date);
        d.date_raw = d.date;
        d.p_date = timeParseNull2(d.p_date);
        d.hedged_date = timeParseNull2(d.hedged_date);
        d.sale_date = timeParseNull2(d.sale_date);
      });
      response.forEach(function(d) {
        delete d.row;
      });
      _this[fieldName] = response;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === COMMODITY: Simulate === //
// export function simulateCommodityPortfolio(
//   _this,
//   fieldName,
//   fieldShowLoadingName
// ) {
//   _this[fieldShowLoadingName] = true;
//
//   let params = {
//     method: "POST",
//     endpoint: "commodity_simulate_portfolio",
//     content_type: "application/json",
//     query_params: {
//       format: "json",
//       commodity: _this.commodityNumber,
//       confidence: _this.confidence / 100,
//       expo: JSON.stringify(_this.expoDataRaw),
//       hedge: JSON.stringify(_this.hedgeDataRaw)
//     }
//   };
//   return apiService
//     .getAll(params)
//     .then(response => {
//       response = JSON.parse(JSON.stringify(response));
//       response.forEach(function(d) {
//         d.date = timeParseNull2(d.date);
//         d.date_raw = d.date;
//         d.p_date = timeParseNull2(d.p_date);
//         d.hedged_date = timeParseNull2(d.hedged_date);
//         d.sale_date = timeParseNull2(d.sale_date);
//       });
//       response.forEach(function(d) {
//         delete d.row;
//       });
//       _this[fieldName] = response;
//       _this[fieldShowLoadingName] = false;
//     })
//     .catch(function(error) {
//       _this[fieldShowLoadingName] = false;
//       console.log(error);
//     });
// }

// === CURRENCY & COMMODITY: Derivatives Valuation === //
export function getDerivativesValuation(
  _this,
  data,
  fieldName,
  fieldShowLoadingName,
  asset = "currencies",
  spotField = "spot",
  analyticsSelectedAnalysis = "",
  customDate = null,
) {
  _this[fieldShowLoadingName] = true;
  _this[fieldName] = 0;
  let analysis =
    analyticsSelectedAnalysis === "valuation"
      ? "custom"
      : _this.pickedAnalysis <= 1 || _this.pickedAnalysis === 4
      ? "now"
      : "future";
  let params = {
    method: "POST",
    endpoint: "currency_derivatives_valuation",
    content_type: "application/json",
    query_params: {
      format: "json",
      spot: _this[spotField],
      derivatives: JSON.stringify(data),
      analysis: analysis,
      asset: asset,
      isCents: _this.isCents,
      customDate: customDate,
    },
  };
  return apiService
    .getAll(params)
    .then((response) => {
      response = JSON.parse(JSON.stringify(response));
      response.forEach(function(d) {
        d.date = timeParseNull2(d.date);
        d.p_date = timeParseNull2(d.p_date);
        d.premium_value_date = timeParseNull2(d.premium_value_date);
        d.trade_date = timeParseNull2(d.trade_date);
        d.value_date = timeParseNull2(d.value_date);
        d.hedged_date = timeParseNull2(d.hedged_date);
        d.sale_date = timeParseNull2(d.sale_date);
        if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
          d.hedgeNotional = 0;
        } else {
          if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
            d.hedgeNotional = 0;
          } else {
            d.hedgeNotional = +d.notional * d.undelying_position;
          }
        } // TODO: puede venir del back
        d.cash_flow = 0;
        d.balance = 0;
      });
      response.forEach(function(d) {
        delete d.row;
      });
      _this[fieldName] = response;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === CURRENCY: program data for currencies === //
export function getCurrencyProgram(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "currency_program",
    query_params: {
      format: "json",
      currency: _this.currencyNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      if (_this.modal_processing_message !== undefined && data.length > 0) {
        let localData = [];
        let hTot = 0;
        _this.radioSelection = data[0].objective.toLowerCase();
        if (data[0].objective === "Balance") {
          _this.selectedBalGradient = data[0].parameters;
          if (_this.selectedBalGradient === "monthly") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.balanceMonths = Object.keys(d.flow.values).length;
              _this.exampleDataRaw.forEach(function(b) {
                if (b.id < _this.balanceMonths) {
                  localData.push({
                    id: b.id,
                    date: "",
                    balance: b.balance,
                    cashFlow: b.cashFlow,
                    hedge:
                      (d.flow.values[b.id] / 100) * (b.balance + b.cashFlow),
                    percentage: d.flow.values[b.id],
                  });
                }
              });
            });
            hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
            _this.balanceTotal = _this.exampleDataRaw
              .map((d) => d.balance)
              .reduce((a, b) => a + b);
            _this.balancePercentage = Math.round(
              (100 * hTot) / _this.balanceTotal,
            );
          }
          if (_this.selectedCashFlowGradient === "quarterly") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.cashFlowMonths = Object.keys(d.flow.values).length * 3;
              let array_percentages = [];
              for (let i in d.flow.values) {
                array_percentages.push(d.flow.values[i]);
              }
              let count = 0;
              _this.exampleDataRaw.forEach(function(b) {
                if (b.id < _this.cashFlowMonths) {
                  if (b.id % 3 === 0 && b.id !== 0) {
                    count = count + 1;
                  }
                  localData.push({
                    id: b.id,
                    date: "",
                    balance: b.balance,
                    cashFlow: b.cashFlow,
                    hedge:
                      (array_percentages[count] / 100) *
                      (b.balance + b.cashFlow),
                    percentage: array_percentages[count],
                  });
                }
              });
            });
            hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
            _this.balanceTotal = _this.exampleDataRaw
              .map((d) => d.balance)
              .reduce((a, b) => a + b);
            _this.balancePercentage = Math.round(
              (100 * hTot) / _this.balanceTotal,
            );
          }
          if (_this.selectedBalGradient === "match") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.balancePercentage = d.flow.values.percentage;
            });
          }
          if (_this.selectedBalGradient === "constant") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.balanceMonths = d.flow.values.months;
              _this.balancePercentage = d.flow.values.percentage;
            });
          }
        } else {
          _this.selectedCashFlowGradient = data[0].parameters;
          if (_this.selectedCashFlowGradient === "monthly") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.cashFlowMonths = Object.keys(d.flow.values).length;
              _this.exampleDataRaw.forEach(function(b) {
                if (b.id < _this.cashFlowMonths) {
                  localData.push({
                    id: b.id,
                    date: "",
                    balance: b.balance,
                    cashFlow: b.cashFlow,
                    hedge:
                      (d.flow.values[b.id] / 100) * (b.balance + b.cashFlow),
                    percentage: d.flow.values[b.id],
                  });
                }
              });
            });
            hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
            _this.balanceTotal = _this.exampleDataRaw
              .map((d) => d.balance)
              .reduce((a, b) => a + b);
            _this.cashFlowTotal = _this.exampleDataRaw
              .map((d) => d.cashFlow)
              .filter((d, i) => i < _this.cashFlowMonths)
              .reduce((a, b) => a + b);
            _this.cashFlowPercentage = Math.round(
              (100 * hTot) / (_this.balanceTotal + _this.cashFlowTotal),
            );
          }
          if (_this.selectedCashFlowGradient === "quarterly") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.cashFlowMonths = Object.keys(d.flow.values).length * 3;
              let array_percentages = [];
              for (let i in d.flow.values) {
                array_percentages.push(d.flow.values[i]);
              }
              let count = 0;
              _this.exampleDataRaw.forEach(function(b) {
                if (b.id < _this.cashFlowMonths) {
                  if (b.id % 3 === 0 && b.id !== 0) {
                    count = count + 1;
                  }
                  localData.push({
                    id: b.id,
                    date: "",
                    balance: b.balance,
                    cashFlow: b.cashFlow,
                    hedge:
                      (array_percentages[count] / 100) *
                      (b.balance + b.cashFlow),
                    percentage: array_percentages[count],
                  });
                }
              });
            });
            hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
            _this.balanceTotal = _this.exampleDataRaw
              .map((d) => d.balance)
              .reduce((a, b) => a + b);
            _this.cashFlowTotal = _this.exampleDataRaw
              .map((d) => d.cashFlow)
              .filter((d, i) => i < _this.cashFlowMonths)
              .reduce((a, b) => a + b);
            _this.cashFlowPercentage = Math.round(
              (100 * hTot) / (_this.balanceTotal + _this.cashFlowTotal),
            );
          }
          if (_this.selectedCashFlowGradient === "budget") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.cashFlowBudgetMonth = d.flow.values.percentagemonths;
              _this.cashFlowPercentage = d.flow.values.percentage;
            });
          }
          if (_this.selectedCashFlowGradient === "constant") {
            data.map(function(d) {
              // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
              d.flow = JSON.parse(d.flow);
              _this.cashFlowMonths = d.flow.values.months;
              _this.cashFlowPercentage = d.flow.values.percentage;
            });
          }
        }
        _this.modal_processing_message.showModalLoading = false;
      } else {
        if (typeof _this.programDataModel !== "undefined") {
          _this.modal_processing_message.messageModal =
            "No program found for this portfolio";
          _this.modal_processing_message.optionsModal = true;
        } else {
          data.forEach(function(d) {
            d.currency = d.fk_currency;
            // d.flow = JSON.parse(d.flow.replace(/'/g, '"'));
          });
          _this[fieldName] = data;
          _this[fieldShowLoadingName] = false;
          // console.log("program file");
          // console.log(data);
        }
      }
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === COMMODITY: program data for commodities === //
export function getCommodityProgram(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_program",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      if (_this.modal_processing_message !== undefined && data.length > 0) {
        let localData = [];
        let hTot = 0;
        _this.selectedCashFlowGradient = data[0].parameters;
        if (_this.selectedCashFlowGradient === "monthly") {
          _this.selectedCashFlowGradient = data[0].parameters;
          data.map(function(d) {
            // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
            d.flow = JSON.parse(d.flow);
            _this.cashFlowMonths = Object.keys(d.flow.values).length;
            _this.exampleDataRaw.forEach(function(b) {
              if (b.id < _this.cashFlowMonths) {
                localData.push({
                  id: b.id,
                  date: "",
                  balance: b.balance,
                  cashFlow: b.cashFlow,
                  hedge: (d.flow.values[b.id] / 100) * (b.balance + b.cashFlow),
                  percentage: d.flow.values[b.id],
                });
              }
            });
          });
          hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
          _this.balanceTotal = _this.exampleDataRaw
            .map((d) => d.balance)
            .reduce((a, b) => a + b);
          _this.cashFlowTotal = _this.exampleDataRaw
            .map((d) => d.cashFlow)
            .filter((d, i) => i < _this.cashFlowMonths)
            .reduce((a, b) => a + b);
          _this.cashFlowPercentage = Math.round(
            (100 * hTot) / (_this.balanceTotal + _this.cashFlowTotal),
          );
        }
        if (_this.selectedCashFlowGradient === "quarterly") {
          data.map(function(d) {
            // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
            d.flow = JSON.parse(d.flow);
            _this.cashFlowMonths = Object.keys(d.flow.values).length * 3;
            let array_percentages = [];
            for (let i in d.flow.values) {
              array_percentages.push(d.flow.values[i]);
            }
            let count = 0;
            _this.exampleDataRaw.forEach(function(b) {
              if (b.id < _this.cashFlowMonths) {
                if (b.id % 3 === 0 && b.id !== 0) {
                  count = count + 1;
                }
                localData.push({
                  id: b.id,
                  date: "",
                  balance: b.balance,
                  cashFlow: b.cashFlow,
                  hedge:
                    (array_percentages[count] / 100) * (b.balance + b.cashFlow),
                  percentage: array_percentages[count],
                });
              }
            });
          });
          _this.exampleData = localData;
          hTot = localData.map((d) => d.hedge).reduce((a, b) => a + b);
          _this.balanceTotal = _this.exampleDataRaw
            .map((d) => d.balance)
            .reduce((a, b) => a + b);
          _this.cashFlowTotal = _this.exampleDataRaw
            .map((d) => d.cashFlow)
            .filter((d, i) => i < _this.cashFlowMonths)
            .reduce((a, b) => a + b);
          _this.cashFlowPercentage = Math.round(
            (100 * hTot) / (_this.balanceTotal + _this.cashFlowTotal),
          );
        }
        if (_this.selectedCashFlowGradient === "budget") {
          data.map(function(d) {
            // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
            d.flow = JSON.parse(d.flow);
            _this.cashFlowBudgetMonth = d.flow.values.percentagemonths;
            _this.cashFlowPercentage = d.flow.values.percentage;
          });
        }
        if (_this.selectedCashFlowGradient === "constant") {
          data.map(function(d) {
            // d.flow = JSON.stringify(d.flow).replace(/'/g, '"');
            d.flow = JSON.parse(d.flow);
            _this.cashFlowMonths = d.flow.values.months;
            _this.cashFlowPercentage = d.flow.values.percentage;
          });
        }
        _this.modal_processing_message.showModalLoading = false;
      } else {
        if (typeof _this.programDataModel !== "undefined") {
          _this.modal_processing_message.messageModal =
            "No program found for this portfolio";
          _this.modal_processing_message.optionsModal = true;
        } else {
          data.forEach(function(d) {
            d.currency = d.fk_idcommodity;
            // d.flow = JSON.parse(d.flow.replace(/'/g, '"'));
          });
          _this[fieldName] = data;
          _this[fieldShowLoadingName] = false;
        }
      }
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === MARKETS COMMODITY: import TABLE DATA === //
export function getCommodityTableData(
  _this,
  fieldName,
  fieldShowLoadingName,
  commodityNumberList,
  units,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_table",
    query_params: {
      format: "json",
      commodity: { id_commodities: commodityNumberList },
      max_contracts: _this.maxContractsPerCommodity,
      units: { id_units: units },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d, i) {
        d.observation_date =
          d.observation_date === null ? "" : timeParseNull2(d.observation_date);
        d.fk_commodities = d.commodity;
        d.id = i;
      });
      _this[fieldName] = data;
      // console.log(_this[fieldName]);
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === MARKETS COMMODITY: import calculated historical commodity data === //
export function getCommodityHistoricalData(
  _this,
  fieldName,
  fieldShowLoadingName,
  lastDataName = "historicalOHLC",
  commodityNumber,
  commodityContract,
  initDate,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodities-technical-analysis",
    query_params: {
      format: "json",
      observation_date: formatDate_YMD(initDate),
      commodity: commodityNumber,
      contract: commodityContract,
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.date = timeParse("%Y-%m-%d")(d.observation_date);
        d.ma40 = d.ma_40;
      });
      let n = data.length - 1;
      if (
        data[n].interp_trend_short === null ||
        data[n].interp_trend_short === undefined
      ) {
        data[n].interp_trend_short = data[n - 1].interp_trend_short;
      }
      if (
        data[n].interp_trend_medium === null ||
        data[n].interp_trend_medium === undefined
      ) {
        data[n].interp_trend_medium = data[n - 1].interp_trend_medium;
      }
      if (
        data[n].interp_trend_long === null ||
        data[n].interp_trend_long === undefined
      ) {
        data[n].interp_trend_long = data[n - 1].interp_trend_long;
      }
      if (
        data[n].ma20 === null ||
        data[n].ma20 === undefined ||
        data[n].ma40 === null ||
        data[n].ma40 === undefined
      ) {
        data[n].ma20 = _this.processData.average(
          data.slice(-20).map((d) => d.close),
        );
        data[n].ma40 = _this.processData.average(
          data.slice(-40).map((d) => d.close),
        );
      }
      _this[fieldName] = data;
      _this[lastDataName] = JSON.parse(JSON.stringify(data[data.length - 1]));
      _this[lastDataName].open = +_this[lastDataName].open;
      _this[lastDataName].high = +_this[lastDataName].high;
      _this[lastDataName].low = +_this[lastDataName].low;
      _this[lastDataName].close = +_this[lastDataName].close;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}
// === MARKETS COMMODITY: import historical commodity data === //
export function getCommoditiesData(
  _this,
  fieldName,
  fieldShowLoadingName,
  commodityNumber,
  commodityContract,
  initDate,
) {
  let params = {
    method: "GET",
    endpoint: "commodities-history",
    query_params: {
      format: "json",
      commodity: commodityNumber,
      contract: commodityContract,
      observation_date: formatDate_YMD(initDate),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.date = timeParse("%Y-%m-%d")(d.observation_date);
        d.asset =
          _this.commodityById[d.fk_commodities] + " " + d.continuous_contract;
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === data model === //

export function getExpoData(_this) {
  _this.expo = [];
  let getPortfolioNumber = [];
  _this.showLoadingExpo = true;
  try {
    _this.selectedPortfolios.forEach(function(selectedPortfolio) {
      getPortfolioNumber.push(
        _this.companies[_this.selectedCompany].portfolios[selectedPortfolio.id]
          .pk_idportfolio,
      );
    });
  } catch (error) {
    _this.showLoadingExpo = false;
    console.log("Portfolio list error", error);
  }

  let getCurrencyNumber = [];
  try {
    for (var crncy in _this.selectedCurrency) {
      getCurrencyNumber.push(
        _this.currencySpecs.filter(
          (curr) => curr.currency_name === _this.selectedCurrency[crncy].name,
        )[0].pk_i_idcurrency,
      );
    }
  } catch (error) {
    _this.showLoadingExpo = false;
  }

  let params = {
    method: "GET",
    endpoint: "currency-exposure",
    query_params: {
      format: "json",
      currency: { id_currencies: getCurrencyNumber },
      portfolios: { id_portfolios: getPortfolioNumber },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.pk =
          _this.companies[_this.selectedCompany].company_name
            .split(/\s/)
            .reduce((response, word) => (response += word.slice(0, 1)), "") +
          d.pk_idcurrencyexposure;
        d.issue_date =
          d.issue_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.issue_date))).format(
                "YYYY-MM-DD",
              );
        d.expiration_date =
          d.expiration_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.expiration_date))).format(
                "YYYY-MM-DD",
              );
        d.planned_expiration_date =
          d.planned_expiration_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.planned_expiration_date)),
              ).format("YYYY-MM-DD");
        d.realized_expiration_date =
          d.realized_expiration_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.realized_expiration_date)),
              ).format("YYYY-MM-DD");
      });
      _this.expo = data;
      _this.showLoadingExpo = false;
      _this.dataSavedExpo = true;
      _this.saveLegendExpo = "Saved:";
      _this.refreshLegendExpo = "Synced:";
    })
    .catch(function(error) {
      _this.showLoadingExpo = false;
      _this.dataSavedExpo = true;
      _this.saveLegendExpo = "Saved:";
      _this.refreshLegendExpo = "Synced:";
      console.log(error);
    });
}

export function getHedgeData(_this) {
  let getPortfolioNumber = [];
  try {
    _this.selectedPortfolios.forEach(function(selectedPortfolio) {
      getPortfolioNumber.push(
        _this.companies[_this.selectedCompany].portfolios[selectedPortfolio.id]
          .pk_idportfolio,
      );
    });
  } catch (error) {
    _this.showLoadingExpo = false;
    console.log("Portfolio list error");
  }

  let getCurrencyNumber = [];
  try {
    for (var crncy in _this.selectedCurrency) {
      getCurrencyNumber.push(
        _this.currencySpecs.filter(
          (curr) => curr.currency_name === _this.selectedCurrency[crncy].name,
        )[0].pk_i_idcurrency,
      );
    }
  } catch (error) {
    _this.showLoadingExpo = false;
    console.log("Currency list error");
  }

  let params = {
    method: "GET",
    endpoint: "currency-hedge",
    query_params: {
      format: "json",
      currency: { id_currencies: getCurrencyNumber },
      portfolios: { id_portfolios: getPortfolioNumber },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.pk =
          _this.companies[_this.selectedCompany].company_name
            .split(/\s/)
            .reduce((response, word) => (response += word.slice(0, 1)), "") +
          d.pk_idcurrencyhedge;
        d.trade_date =
          d.trade_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.trade_date))).format(
                "YYYY-MM-DD",
              );
        d.fixing_date =
          d.fixing_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.fixing_date))).format(
                "YYYY-MM-DD",
              );
        d.value_date =
          d.value_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.value_date))).format(
                "YYYY-MM-DD",
              );
        d.premium_value_date =
          d.premium_value_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.premium_value_date)),
              ).format("YYYY-MM-DD");
        d.asian_fixing_start_date =
          d.asian_fixing_start_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.asian_fixing_start_date)),
              ).format("YYYY-MM-DD");

        d.buy_sell = longShortTransform(d.buy_sell.toUpperCase());
        d.derivative = d.derivative.toUpperCase();
        d.settlement_type = d.settlement_type.replace("_", " ").toUpperCase();
        d.fk_idcurrencystrategy_derivative = d.fk_idcurrencystrategy_derivative.toUpperCase();
        d.strategy_position = d.fk_idcurrencystrategy_derivative.substr(
          0,
          d.fk_idcurrencystrategy_derivative.indexOf(" "),
        );
        d.strategy_name = d.fk_idcurrencystrategy_derivative.substr(
          d.fk_idcurrencystrategy_derivative.indexOf(" ") + 1,
        );
        d.fk_idoptiontype = _this.optionTypes[d.fk_idoptiontype - 1].name;
      });
      _this.hedge = data;
      _this.showLoadingHedge = false;
      _this.dataSavedHedge = true;
      _this.saveLegendHedge = "Saved:";
      _this.refreshLegendHedge = "Synced:";
    })
    .catch(function(error) {
      _this.showLoadingHedge = false;
      console.log(error);
    });
}

export function getExpoDataCommodities(_this) {
  let getPortfolioNumber = [];
  try {
    _this.selectedPortfolios.forEach(function(selectedPortfolio) {
      getPortfolioNumber.push(
        _this.companies[_this.selectedCompany].portfolios[selectedPortfolio.id]
          .pk_idportfolio,
      );
    });
  } catch (error) {
    console.log("Portfolio list error");
  }

  let getCommodityNumber = [];
  try {
    for (var commodity in _this.selectedCommodity) {
      getCommodityNumber.push(
        _this.commoditySpecs.filter(
          (com) =>
            com.fk_idproduct.toUpperCase() ===
            _this.selectedCommodity[commodity].name,
        )[0].pk_idcommodity,
      );
    }
  } catch (error) {
    console.log("Commodity list error");
  }

  let params = {
    method: "GET",
    endpoint: "commodity-exposure",
    query_params: {
      format: "json",
      commodity: { id_commodities: getCommodityNumber },
      portfolios: { id_portfolios: getPortfolioNumber },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.pk =
          _this.companies[_this.selectedCompany].company_name
            .split(/\s/)
            .reduce((response, word) => (response += word.slice(0, 1)), "") +
          d.pk_idcommodity_exposure;
        d.continuation_month =
          d.continuation_month === 0 ? "" : d.continuation_month;
        d.fk_idcommodity = d.fk_idcommodity.toUpperCase();
        d.quotation = d.commodity_currency + "/" + d.commodity_unit;
        d.issue_date =
          d.issue_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.issue_date))).format(
                "YYYY-MM-DD",
              );
        d.expiration_date =
          d.expiration_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.expiration_date))).format(
                "YYYY-MM-DD",
              );
        d.planned_expiration_date =
          d.planned_expiration_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.planned_expiration_date)),
              ).format("YYYY-MM-DD");
        d.realized_expiration_date =
          d.realized_expiration_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.realized_expiration_date)),
              ).format("YYYY-MM-DD");
      });
      _this.expoCommodities = data;
      _this.showLoadingExpoCommodities = false;
      _this.dataSavedExpoCommodities = true;
      _this.saveLegendExpoCommodities = "Saved:";
      _this.refreshLegendExpoCommodities = "Synced:";
    })
    .catch(function(error) {
      _this.showLoadingExpoCommodities = false;
      _this.dataSavedExpoCommodities = true;
      _this.saveLegendExpoCommodities = "Saved:";
      _this.refreshLegendExpoCommodities = "Synced:";
      console.log(error);
    });
}

export function getHedgeDataCommodities(_this) {
  let getPortfolioNumber = [];
  try {
    _this.selectedPortfolios.forEach(function(selectedPortfolio) {
      getPortfolioNumber.push(
        _this.companies[_this.selectedCompany].portfolios[selectedPortfolio.id]
          .pk_idportfolio,
      );
    });
  } catch (error) {
    console.log("Portfolio list error");
  }
  let getCommodityNumber = [];
  try {
    for (var commodity in _this.selectedCommodity) {
      getCommodityNumber.push(
        _this.commoditySpecs.filter(
          (com) =>
            com.fk_idproduct.toUpperCase() ===
            _this.selectedCommodity[commodity].name,
        )[0].pk_idcommodity,
      );
    }
  } catch (error) {
    console.log("Commodity list error");
  }

  let params = {
    method: "GET",
    endpoint: "commodity-hedge",
    query_params: {
      format: "json",
      commodity: { id_commodities: getCommodityNumber },
      portfolios: { id_portfolios: getPortfolioNumber },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.pk =
          _this.companies[_this.selectedCompany].company_name
            .split(/\s/)
            .reduce((response, word) => (response += word.slice(0, 1)), "") +
          d.pk_idcommodityhedge;
        d.quotation = d.commodity_currency + "/" + d.commodity_unit;
        d.continuation_month =
          d.continuation_month === 0 ? "" : d.continuation_month;
        d.trade_date =
          d.trade_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.trade_date))).format(
                "YYYY-MM-DD",
              );
        d.fixing_date =
          d.fixing_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.fixing_date))).format(
                "YYYY-MM-DD",
              );
        d.value_date =
          d.value_date === null
            ? ""
            : moment(String(timeParse("%Y-%m-%d")(d.value_date))).format(
                "YYYY-MM-DD",
              );
        d.premium_value_date =
          d.premium_value_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.premium_value_date)),
              ).format("YYYY-MM-DD");
        d.asian_fixing_start_date =
          d.asian_fixing_start_date === null
            ? ""
            : moment(
                String(timeParse("%Y-%m-%d")(d.asian_fixing_start_date)),
              ).format("YYYY-MM-DD");
        d.buy_sell = longShortTransform(d.buy_sell.toUpperCase());
        d.derivative = d.derivative.toUpperCase();
        d.settlement_type = d.settlement_type.replace("_", " ").toUpperCase();
        d.fk_idcommodity = d.fk_idcommodity.toUpperCase();
        d.fk_idcommodity_strategy = d.fk_idcommodity_strategy.toUpperCase();
        d.strategy_position = d.fk_idcommodity_strategy.substr(
          0,
          d.fk_idcommodity_strategy.indexOf(" "),
        );
        d.strategy_name = d.fk_idcommodity_strategy.substr(
          d.fk_idcommodity_strategy.indexOf(" ") + 1,
        );
        d.fk_idoptiontype = _this.optionTypes[d.fk_idoptiontype - 1].name;
      });
      _this.hedgeCommodities = data;
      _this.showLoadingComHedge = false;
      _this.dataSavedHedgeCommodities = true;
      _this.saveLegendHedgeCommodities = "Saved:";
      _this.refreshLegendHedgeCommodities = "Synced:";
    })
    .catch(function(error) {
      _this.showLoadingHedge = false;
      console.log(error);
    });
}

export function getExpoOptionsData(_this) {
  let params = {
    method: "GET",
    endpoint: "exposure-option",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this.expoOptions = data; // = data.map(d => d.name);
      let txt = "One of the following: ";
      _this.expoOptions.forEach((d, i) =>
        i < _this.expoOptions.length - 1
          ? (txt += d.name + ", ")
          : (txt += d.name + "."),
      );
      var index = _this.headersExpo1
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.headersExpo1[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to headersExpo1");
      }
      index = _this.mustHeadersExpo
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.mustHeadersExpo[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to mustHeadersExpo");
      }
    })
    .catch(function(error) {
      console.log(error);
    });
}

export function getOptionTypesData(_this) {
  let params = {
    method: "GET",
    endpoint: "option-type",
    query_params: {
      format: "json",
    },
  };
  console.log("getOptionTypesData");
  return apiService
    .getAll(params)
    .then((data) => {
      _this.optionTypes = data;
      console.log("getOptionTypesData");
      console.log(_this.optionTypes);
      let txt = "One of the following: ";
      _this.optionTypes.forEach((d, i) =>
        i < _this.optionTypes.length - 1
          ? (txt += d.name + ", ")
          : (txt += d.name + "."),
      );
      var index = _this.headersHedge1
        .map(function(d) {
          return d.text;
        })
        .indexOf("Option Type");
      if (index > -1) {
        _this.headersHedge2[index].explain = txt;
      } else {
        console.log("Could not load optionTypes to headersHedge1");
      }
      index = _this.mustHeadersHedge
        .map(function(d) {
          return d.text;
        })
        .indexOf("Option Type");
      if (index > -1) {
        _this.mustHeadersHedge[index].explain = txt;
      } else {
        console.log("Could not load optionTypes to mustHeadersHedge");
      }
    })
    .catch(function(error) {
      console.log(error);
    });
}

export function getStrategiesData(_this, fieldName, fieldShowLoadingName) {
  let params = {
    method: "GET",
    endpoint: "currency_strategy",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.name = d.strategy_position.toUpperCase() + " " + d.fk_idstrategyname;
        d.calculated = false;
        d.simulated = false;
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === data Model APIs for commodities === //
export function getExpoOptionsDataCommodities(_this) {
  let params = {
    method: "GET",
    endpoint: "exposure-option-commodities",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this.expoOptionsCommodities = data; // = data.map(d => d.name);
      let txt = "One of the following: ";
      _this.expoOptionsCommodities.forEach((d, i) =>
        i < _this.expoOptionsCommodities.length - 1
          ? (txt += d.name + ", ")
          : (txt += d.name + "."),
      );
      /*
      var index = _this.headersExpo1
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.headersExpo1[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to headersExpo1");
      }
      index = _this.mustHeadersExpo
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.mustHeadersExpo[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to mustHeadersExpo");
      }
      */
    })
    .catch(function(error) {
      console.log(error);
    });
}

export function getPricingMethods(_this) {
  let params = {
    method: "GET",
    endpoint: "pricing-methods",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this.pricingMethods = data; // = data.map(d => d.name);
      let txt = "One of the following: ";
      _this.pricingMethods.forEach((d, i) =>
        i < _this.pricingMethods.length - 1
          ? (txt += d.name + ", ")
          : (txt += d.name + "."),
      );
      /*
      var index = _this.headersExpo1
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.headersExpo1[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to headersExpo1");
      }
      index = _this.mustHeadersExpo
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.mustHeadersExpo[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to mustHeadersExpo");
      }
      */
    })
    .catch(function(error) {
      console.log(error);
    });
}

export function getIncoterms(_this) {
  let params = {
    method: "GET",
    endpoint: "incoterms",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this.incoterms = data; // = data.map(d => d.name);
      let txt = "One of the following: ";
      _this.incoterms.forEach((d, i) =>
        i < _this.incoterms.length - 1
          ? (txt += d.name + ", ")
          : (txt += d.name + "."),
      );
      _this.incoterms.unshift({
        explanation: "",
        name: "",
        pk_idincoterms: -1,
      });
      /*
      var index = _this.headersExpo1
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.headersExpo1[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to headersExpo1");
      }
      index = _this.mustHeadersExpo
        .map(function(d) {
          return d.text;
        })
        .indexOf("Exposure Type");
      if (index > -1) {
        _this.mustHeadersExpo[index].explain = txt;
      } else {
        console.log("Could not load expoOptions to mustHeadersExpo");
      }
      */
    })
    .catch(function(error) {
      console.log(error);
    });
}

// === markets APIs for commodities === //
export function readCommoditiesSpecs(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodities",
    query_params: {
      format: "json",
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === markets APIs for ETFs === //
export function readETFSpecs(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "etfs",
    query_params: { format: "json" },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === markets APIs for indexes === //
export function readIndexSpecs(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "indexes",
    query_params: { format: "json" },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === markets APIs for futures === //
export function readFuturesSpecs(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "futures",
    query_params: { format: "json" },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === markets APIs for interest rates === //
export function readIRSpecs(_this, fieldName, fieldShowLoadingName) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "interest-rates",
    query_params: { format: "json" },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

/* ====== DATA FOR COMMODITIES  ========*/
// === COMMODITIES: exposure data, with Fwd Valuation === //
export function getFutureExposureCommodityData(
  _this,
  fieldName,
  fieldShowLoadingName,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_future_exposure",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
      date: formatDate_YMD(new Date()),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data = JSON.parse(JSON.stringify(data));
      data.forEach(function(d) {
        d.date = timeParseNull2(d.date);
        d.p_date = timeParseNull2(d.p_date);
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === COMMODITIES: exposure data, with Fwd Valuation past === //

export function getPastExposureCommodityData(
  _this,
  fieldName,
  fieldShowLoadingName,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_past_exposure",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
      date: formatDate_YMD(new Date()),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.date = timeParseNull2(d.date);
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// === COMMODITY: hedging data, with Derivatives Valuation === //
export function getFutureHedgeCommodityData(
  _this,
  fieldName,
  fieldShowLoadingName,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_future_hedge",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
      date: formatDate_YMD(new Date()),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data = JSON.parse(JSON.stringify(data));
      data.forEach(function(d) {
        d.date = timeParseNull2(d.fixing_date);
        d.premium_value_date = timeParseNull2(d.premium_value_date);
        d.trade_date = timeParseNull2(d.trade_date);
        d.value_date = timeParseNull2(d.value_date);
        d.notional = +d.quantity;
        d.hedgeNotional = +d.quantity * d.undelying_position; // TODO: puede venir del back
        d.cash_flow = 0;
        d.balance = 0;
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

export function getPastHedgeCommodityData(
  _this,
  fieldName,
  fieldShowLoadingName,
) {
  _this[fieldShowLoadingName] = true;
  let params = {
    method: "GET",
    endpoint: "commodity_past_hedge",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
      date: formatDate_YMD(new Date()),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      data.forEach(function(d) {
        d.date = timeParseNull2(d.fixing_date);
        d.premium_value_date = timeParseNull2(d.premium_value_date);
        d.trade_date = timeParseNull2(d.trade_date);
        d.value_date = timeParseNull2(d.value_date);
        d.hedgeNotional = +d.quantity * d.undelying_position; // TODO: puede venir del back
        d.notional = +d.quantity;
        d.cash_flow = 0;
        d.balance = 0;
      });
      _this[fieldName] = data;
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

export function getCommodityBudgetData(
  _this,
  fieldName,
  fieldUpdatedAtName,
  fieldShowLoadingName,
  fieldUpToDate,
) {
  _this[fieldShowLoadingName] = true;
  _this[fieldName] = 0;
  let params = {
    method: "GET",
    endpoint: "get_currency_budget",
    query_params: {
      format: "json",
      commodity: _this.commodityNumber,
      portfolios: { id_portfolios: _this.portfolioNumbers },
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      if (data.length > 0) {
        _this[fieldName] = data[0].budget_rate;
        _this[fieldUpdatedAtName] =
          "Saved at: " + formatDate_DDMMMYHMS(data[0].created_at);
      } else {
        _this[fieldName] = 0;
        _this[fieldUpdatedAtName] =
          "Created at: " + formatDate_DDMMMYHMS(new Date());
      }
      _this[fieldShowLoadingName] = false;
      _this[fieldUpToDate] = true;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}

// export function getFunctionByAsset

// === OPTIMIZER CURRENCY: import currency valuation inputs === //
export function readCurrencyValuationDataOptimizer(
  _this,
  fieldName,
  fieldShowLoadingName,
  params,
  methods = {},
) {
  _this[fieldShowLoadingName] = true;
  return apiService
    .getAll(params)
    .then((data) => {
      let keys = Object.keys(methods);
      _this[fieldName] = data;
      if (keys.length !== 0) {
        for (let key of keys) {
          methods[key](_this);
        }
      }
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === OPTIMIZER CURRENCY: import commodity valuation inputs === //
export function readCommodityValuationDataOptimizer(
  _this,
  fieldName,
  fieldShowLoadingName,
  params,
  methods = {},
) {
  _this[fieldShowLoadingName] = true;
  return apiService
    .getAll(params)
    .then((data) => {
      let keys = Object.keys(methods);
      _this[fieldName] = data;
      if (keys.length !== 0) {
        for (let key of keys) {
          methods[key](_this);
        }
      }
      _this[fieldShowLoadingName] = false;
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      _this[fieldName] = [];
      console.log(error);
    });
}

// === OPTIMIZER: update strategy === //
export function updateStrategyValues(
  _this,
  strategy,
  idx,
  cost,
  isStrip,
  editedLeg,
  editedIsStrip,
  editedCurrAve,
  editedLegStripChanged,
  asset = "currencies",
) {
  let params = {
    method: "POST",
    endpoint: "update_strategy",
    content_type: "application/json",
    query_params: {
      format: "json",
      strategy: JSON.stringify([strategy]),
      asset: asset,
      spot: _this.spot,
      objective_cost: cost,
      userCosts: _this.userCosts,
      fixed_cost: _this.strategies[idx].fixedCost,
      leg: _this.strategies[idx].objectiveLeg,
      is_strip: isStrip,
      edited_leg: editedLeg,
      edited_is_strip: editedIsStrip,
      edited_strip_changed: editedLegStripChanged,
      edited_leg_average: editedCurrAve,
      data: JSON.stringify(_this.strategies[idx].data),
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      let summary = JSON.parse(JSON.stringify(data[0]["summary"]));
      summary[0]["isStrip1"] = _this.strategies[idx].isStrip1;
      summary[0]["isStrip2"] = _this.strategies[idx].isStrip2;
      summary[0]["isStrip3"] = _this.strategies[idx].isStrip3;
      summary[0]["isStrip4"] = _this.strategies[idx].isStrip4;
      data = JSON.parse(JSON.stringify(data[0]["all"]));
      data.forEach(function(d) {
        d.date = timeParseNull2(d.date);
        d.premium_value_date = timeParseNull2(d.premium_value_date);
        d.trade_date = timeParseNull2(d.trade_date);
        d.value_date = timeParseNull2(d.value_date);
        d.cash_flow = 0;
        d.balance = 0;
      });
      data.forEach(function(d) {
        delete d.row;
      });
      _this.strategies[idx].data = data;
      _this.strategies[idx].simulated = false;
      _this.strategies[idx].simulations = [];
      _this.hedgeData = data;
      createExcelForStrategy(_this, data, strategy);
      _this.strategies[idx].calculated = true;
      _this.minMaxYAxisRates = _this.processData.minMax(
        _this.hedgeData,
        "strike",
        "strike",
        [_this.minMaxYAxisHist[0], _this.minMaxYAxisHist[1]],
      );
      _this.minMaxYAxisRates = _this.processData.minMax(
        _this.hedgeData,
        "fwd",
        "fwd",
        [_this.minMaxYAxisRates[0], _this.minMaxYAxisRates[1]],
      );
      _this.minMaxYAxisRates = additionalYSpace(_this.minMaxYAxisRates, 0.05);
      _this.renderCharts += 1;
      writeStrategySummary(_this, idx, summary);
      _this.selectedStrategy = _this.strategies[idx];
      _this.strategies[idx].showLoading = false;
      highlightOnOff(_this, "highlightBorder");
      readyToSimulate(_this);
    })
    .catch(function(error) {
      _this.strategies[idx].showLoading = false;
      console.log(error);
    });
}

// === OPTIMIZER: Optimize === //
export function optimizeHedge(_this, args, fieldShowLoadingName) {
  // console.log("optimizeHedge");
  _this[fieldShowLoadingName] = true;
  let endpoint = "optimize_currencies";
  // _this.selectedAssetLink === 0
  //   ? "optimize_currencies"
  //   : "optimize_commodities";

  if (_this.strategies.length <= 1) {
    return;
  }
  let cost = _this.strategies.map((d) => d.cost);
  let x0 = _this.strategies.map((d) => d.percentage);

  let params = {
    method: "POST",
    endpoint: endpoint,
    content_type: "application/json",
    query_params: {
      format: "json",
      simulations: JSON.stringify(_this.simulations),
      position: _this.selectedPosition,
      confidence: _this.confidence,
      optimization_type: args["optimization_type"],
      var_restriction: args["var_restriction"],
      cost_restriction: args["cost_restriction"],
      costs: JSON.stringify(cost),
      x0: JSON.stringify(x0),
      locked: _this.portfolioPercLocked,
    },
  };
  return apiService
    .getAll(params)
    .then((data) => {
      _this[fieldShowLoadingName] = false;
      let percentageById = {};
      _this.strategies.forEach(function(d, i) {
        d.percentage = 100 * data[i].result;
        percentageById[d.id] = d.percentage;
      });
      buildPortfolioSimulations(_this, percentageById);
      highlightOnOff(_this, "highlightBorder");
    })
    .catch(function(error) {
      _this[fieldShowLoadingName] = false;
      console.log(error);
    });
}
